import client from 'client'
import type {
  GetAssetFilterOptionsAssetFilterOptionsGetQueryResponse,
  GetAssetFilterOptionsAssetFilterOptionsGetQueryParams,
  GetAssetFilterOptionsAssetFilterOptionsGet422,
} from '../../types/GetAssetFilterOptionsAssetFilterOptionsGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getAssetFilterOptionsAssetFilterOptionsGetQueryKey = (params?: GetAssetFilterOptionsAssetFilterOptionsGetQueryParams) =>
  [{ url: '/asset/filter_options' }, ...(params ? [params] : [])] as const

export type GetAssetFilterOptionsAssetFilterOptionsGetQueryKey = ReturnType<typeof getAssetFilterOptionsAssetFilterOptionsGetQueryKey>

/**
 * @description Get distinct values and counts for asset filter fields, scoped by current filter selections.This endpoint supports cascading/faceted filtering where:- Each facet returns all available values for that field- Counts reflect how many assets match with all OTHER filters applied- Gateway-only facets (configuration_state, connectivity) return null when no gateways match**Usage:**Call this endpoint whenever a filter selection changes to update the available optionsfor other filter dropdowns.
 * @summary Get available filter options for assets
 * {@link /asset/filter_options}
 */
export async function getAssetFilterOptionsAssetFilterOptionsGet(
  params?: GetAssetFilterOptionsAssetFilterOptionsGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    GetAssetFilterOptionsAssetFilterOptionsGetQueryResponse,
    ResponseErrorConfig<GetAssetFilterOptionsAssetFilterOptionsGet422>,
    unknown
  >({ method: 'GET', url: `/asset/filter_options`, params, ...requestConfig })
  return res
}

export function getAssetFilterOptionsAssetFilterOptionsGetQueryOptions(
  params?: GetAssetFilterOptionsAssetFilterOptionsGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const queryKey = getAssetFilterOptionsAssetFilterOptionsGetQueryKey(params)
  return queryOptions<
    ResponseConfig<GetAssetFilterOptionsAssetFilterOptionsGetQueryResponse>,
    ResponseErrorConfig<GetAssetFilterOptionsAssetFilterOptionsGet422>,
    ResponseConfig<GetAssetFilterOptionsAssetFilterOptionsGetQueryResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getAssetFilterOptionsAssetFilterOptionsGet(params, config)
    },
  })
}

/**
 * @description Get distinct values and counts for asset filter fields, scoped by current filter selections.This endpoint supports cascading/faceted filtering where:- Each facet returns all available values for that field- Counts reflect how many assets match with all OTHER filters applied- Gateway-only facets (configuration_state, connectivity) return null when no gateways match**Usage:**Call this endpoint whenever a filter selection changes to update the available optionsfor other filter dropdowns.
 * @summary Get available filter options for assets
 * {@link /asset/filter_options}
 */
export function useGetAssetFilterOptionsAssetFilterOptionsGet<
  TData = ResponseConfig<GetAssetFilterOptionsAssetFilterOptionsGetQueryResponse>,
  TQueryData = ResponseConfig<GetAssetFilterOptionsAssetFilterOptionsGetQueryResponse>,
  TQueryKey extends QueryKey = GetAssetFilterOptionsAssetFilterOptionsGetQueryKey,
>(
  params?: GetAssetFilterOptionsAssetFilterOptionsGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetAssetFilterOptionsAssetFilterOptionsGetQueryResponse>,
        ResponseErrorConfig<GetAssetFilterOptionsAssetFilterOptionsGet422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getAssetFilterOptionsAssetFilterOptionsGetQueryKey(params)

  const query = useQuery({
    ...(getAssetFilterOptionsAssetFilterOptionsGetQueryOptions(params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetAssetFilterOptionsAssetFilterOptionsGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}